import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Play, Edit3, ListVideo, Clock, GripVertical, Save, X, Globe, Youtube, FileText, MonitorPlay } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { thumbUrl } from "@/lib/imageThumb";
import { MenuRenderer } from "@/components/app/MenuRenderer";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

export default function Playlists() {
  const [playlists, setPlaylists] = useState([]);
  const [editing, setEditing] = useState(null);     // playlist being edited
  const [editorOpen, setEditorOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    const r = await api.get("/playlists");
    setPlaylists(r.data.playlists || []);
  };
  useEffect(() => { load(); }, []);

  const createPlaylist = async () => {
    if (!newName) return;
    try {
      const r = await api.post("/playlists", { name: newName, items: [] });
      toast.success("Playlist created");
      setNewName(""); setCreateOpen(false); load();
      openEditor(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEditor = async (pl) => {
    try {
      const r = await api.get(`/playlists/${pl.id}`);
      setEditing(r.data);
      setEditorOpen(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removePlaylist = async (id) => {
    if (!window.confirm("Delete this playlist?")) return;
    try { await api.delete(`/playlists/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-7xl text-slate-900">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3">Playlists</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
              Rotate <span style={accentFont} className="text-sky-600">content</span> by the second
            </h1>
            <p className="mt-3 text-sm text-slate-500 max-w-xl">Combine designs, images, videos and YouTube links into a looping playlist. Each item can have its own duration and time window.</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm" data-testid="new-playlist-btn">
                <Plus className="h-4 w-4 mr-1.5" />New playlist
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 text-slate-900">
              <DialogHeader><DialogTitle style={headingFont}>Create playlist</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Playlist name" className="bg-slate-50 border-slate-200 text-slate-900" data-testid="playlist-name-input"/>
                <Button onClick={createPlaylist} className="w-full rounded-full bg-sky-500 hover:bg-sky-600" data-testid="playlist-create-btn">Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {playlists.length === 0 ? (
          <div className="rounded-3xl border border-dashed p-12 text-center" style={{ background: "#F8FAFC", borderColor: "#E2E8F0" }} data-testid="empty-playlists">
            <ListVideo className="h-10 w-10 mx-auto text-slate-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2" style={headingFont}>No playlists yet</h3>
            <p className="text-sm text-slate-500 max-w-sm mx-auto mb-5">Create one to play multiple designs, images and videos on rotation.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="playlists-grid">
            {playlists.map((p) => (
              <div key={p.id} className="rounded-3xl border bg-white border-slate-200 backdrop-blur-xl p-5 hover:-translate-y-0.5 hover:border-sky-300 transition-all" data-testid={`playlist-${p.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold truncate" style={headingFont}>{p.name}</h3>
                    <div className="text-xs text-slate-500 mt-1">{(p.items || []).length} items · loop {p.loop ? "on" : "off"}</div>
                  </div>
                  <ListVideo className="h-5 w-5 text-sky-600 flex-shrink-0" />
                </div>
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" className="rounded-full flex-1 bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={() => openEditor(p)} data-testid={`edit-playlist-${p.id}`}>
                    <Edit3 className="h-4 w-4 mr-1" />Edit
                  </Button>
                  <Button variant="outline" size="icon" className="rounded-full bg-slate-50 border-slate-200 text-rose-500 hover:bg-rose-50 hover:text-rose-700 w-10 h-10 shrink-0" onClick={() => removePlaylist(p.id)} data-testid={`delete-playlist-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PlaylistEditor
        open={editorOpen}
        onClose={() => { setEditorOpen(false); load(); }}
        playlist={editing}
        onSaved={(updated) => { setEditing(updated); load(); }}
      />
    </DashboardLayout>
  );
}

function PlaylistEditor({ open, onClose, playlist, onSaved }) {
  const [name, setName]   = useState("");
  const [loop, setLoop]   = useState(true);
  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKind, setPickerKind] = useState("asset");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!playlist) return;
    setName(playlist.name || "");
    setLoop(playlist.loop !== false);
    setItems(playlist.items || []);
  }, [playlist]);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/assets"), api.get("/designs")])
      .then(([a, d]) => { setAssets(a.data.assets || []); setDesigns(d.data.designs || []); });
  }, [open]);

  const save = async () => {
    if (!playlist) return;
    setSaving(true);
    try {
      const r = await api.put(`/playlists/${playlist.id}`, { name, loop, items });
      toast.success("Saved");
      onSaved(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const addItem = (item) => {
    setItems([...items, { duration_seconds: 10, ...item }]);
    setPickerOpen(false);
  };

  const updateItem = (idx, patch) => {
    setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const moveItem = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const next = [...items];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setItems(next);
  };

  const addUrlAdHoc = () => {
    setUrlPickerOpen(true);
  };

  const [urlPickerOpen, setUrlPickerOpen] = useState(false);
  const addUrlItem = (item) => {
    addItem(item);
    setUrlPickerOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={headingFont}>Edit playlist</DialogTitle>
          <DialogDescription className="text-slate-500">Drag items to reorder, set how long each plays, and add designs, assets, or web URLs.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-3 items-center">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Playlist name" className="bg-slate-50 border-slate-200 text-slate-900" data-testid="playlist-edit-name"/>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} className="accent-sky-500" />
              Loop
            </label>
          </div>

          <div className="border border-slate-200 rounded-2xl p-3 space-y-2 bg-slate-50">
            {items.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">No items yet — add one below.</div>
            ) : items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl p-3" data-testid={`playlist-item-${idx}`}>
                <div className="flex flex-col text-slate-400">
                  <button onClick={() => moveItem(idx, -1)} className="hover:text-slate-900">▲</button>
                  <button onClick={() => moveItem(idx, 1)} className="hover:text-slate-900">▼</button>
                </div>
                <div className="h-12 w-16 rounded-md bg-slate-50 overflow-hidden flex items-center justify-center text-slate-400 text-[10px] shrink-0">
                  <ItemThumb item={it} designs={designs} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{it.name || it.url || it.ref_id}</div>
                  <div className="text-[11px] text-slate-400">{it.kind}</div>
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Clock className="h-3.5 w-3.5" />
                  <Input
                    type="number"
                    min="1"
                    value={it.duration_seconds}
                    onChange={(e) => updateItem(idx, { duration_seconds: parseInt(e.target.value, 10) || 1 })}
                    className="w-16 h-7 text-xs bg-slate-50 border-slate-200 text-slate-900"
                    data-testid={`item-duration-${idx}`}
                  />s
                </div>
                <div className="flex items-center gap-1 text-[10px] text-slate-500">
                  <Input
                    placeholder="HH:MM"
                    value={it.start_time || ""}
                    onChange={(e) => updateItem(idx, { start_time: e.target.value })}
                    className="w-16 h-7 text-[11px] bg-slate-50 border-slate-200 text-slate-900"
                  />–
                  <Input
                    placeholder="HH:MM"
                    value={it.end_time || ""}
                    onChange={(e) => updateItem(idx, { end_time: e.target.value })}
                    className="w-16 h-7 text-[11px] bg-slate-50 border-slate-200 text-slate-900"
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
                  <X className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => { setPickerKind("asset"); setPickerOpen(true); }} variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" data-testid="add-asset-btn">
              <Plus className="h-4 w-4 mr-1.5" />Add asset
            </Button>
            <Button onClick={() => { setPickerKind("design"); setPickerOpen(true); }} variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" data-testid="add-design-btn">
              <Plus className="h-4 w-4 mr-1.5" />Add design
            </Button>
            <Button onClick={addUrlAdHoc} variant="outline" className="rounded-full bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100" data-testid="add-url-btn">
              <Globe className="h-4 w-4 mr-1.5" />Add web URL
            </Button>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Button variant="outline" onClick={onClose} className="rounded-full bg-slate-50 border-slate-200 text-slate-900">Close</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-sky-500 hover:bg-sky-600" data-testid="playlist-save-btn">
              <Save className="h-4 w-4 mr-1.5" />{saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        {/* Picker dialog */}
        <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={headingFont}>Pick {pickerKind === "asset" ? "an asset" : "a design"}</DialogTitle>
              <DialogDescription className="text-slate-500">Choose what to add to this playlist.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-3">
              {(pickerKind === "asset" ? assets : designs).map((it) => (
                <button
                  key={it.id}
                  onClick={() => addItem(pickerKind === "asset"
                    ? { kind: "asset", ref_id: it.id, name: it.name, thumbnail_url: it.url, duration_seconds: 10, asset_kind: it.kind }
                    : { kind: "design", ref_id: it.id, name: it.name, thumbnail_url: it.config?.banner_image, duration_seconds: 15 })}
                  className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50 text-left hover:border-sky-400 transition-all"
                  data-testid={`pick-${pickerKind}-${it.id}`}
                >
                  <div className="aspect-[4/3] bg-slate-50 overflow-hidden">
                    {pickerKind === "asset" ? (
                      it.kind === "video"
                        ? <video src={it.url} className="w-full h-full object-cover" muted preload="metadata" />
                        : <img src={thumbUrl(it.url, 320)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    ) : (
                      <DesignThumb design={it} />
                    )}
                  </div>
                  <div className="p-2 text-xs truncate">{it.name}</div>
                </button>
              ))}
              {(pickerKind === "asset" ? assets : designs).length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-400">
                  {pickerKind === "asset" ? "No assets yet — upload some on the Media Library page." : "No designs yet — create one from Templates."}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Web URL picker — opened from the "Add web URL" button. Replaces the
            old window.prompt() with a real form that infers a friendly name
            from the URL, offers one-click presets and previews YouTube embeds. */}
        <WebURLPickerDialog
          open={urlPickerOpen}
          onClose={() => setUrlPickerOpen(false)}
          onAdd={addUrlItem}
        />
      </DialogContent>
    </Dialog>
  );
}

// ─── Web URL Picker ─────────────────────────────────────────────────────────
// A polished alternative to window.prompt for adding any web page (YouTube,
// Google Slides, a live dashboard, a social wall, etc.) to a playlist.
const URL_PRESETS = [
  { id: "blank", icon: Globe,       label: "Custom URL",     url: "" },
  { id: "yt",    icon: Youtube,     label: "YouTube",        url: "https://www.youtube.com/watch?v=" },
  { id: "slide", icon: FileText,    label: "Google Slides",  url: "https://docs.google.com/presentation/d/e/.../embed?start=true&loop=true" },
  { id: "live",  icon: MonitorPlay, label: "Live dashboard", url: "https://" },
];

function _inferName(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host.includes("youtube") || host.includes("youtu.be")) return "YouTube video";
    if (host.includes("docs.google.com")) return "Google Slides";
    if (host.includes("twitter.com") || host === "x.com") return "X / Twitter feed";
    return host;
  } catch {
    return url.slice(0, 60);
  }
}

function WebURLPickerDialog({ open, onClose, onAdd }) {
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [duration, setDuration] = useState(15);
  const [touchedName, setTouchedName] = useState(false);

  useEffect(() => {
    if (!open) {
      setUrl(""); setName(""); setDuration(15); setTouchedName(false);
    }
  }, [open]);

  useEffect(() => {
    if (!touchedName) setName(_inferName(url));
  }, [url, touchedName]);

  const submit = () => {
    let trimmed = url.trim();
    if (!trimmed) return;
    if (!/^https?:\/\//i.test(trimmed)) trimmed = "https://" + trimmed;
    try { new URL(trimmed); } catch {
      toast.error("That doesn't look like a valid URL");
      return;
    }
    onAdd({ kind: "url", url: trimmed, name: name || trimmed, duration_seconds: Math.max(1, parseInt(duration, 10) || 15) });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl" data-testid="url-picker-dialog">
        <DialogHeader>
          <DialogTitle style={headingFont}>Add a web URL</DialogTitle>
          <DialogDescription className="text-slate-500">
            Drop in any URL — a YouTube video, your live sales dashboard, a Google Slides deck, a social feed, your storefront — and it&apos;ll rotate inside this playlist.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="url-presets">
            {URL_PRESETS.map((p) => {
              const Icon = p.icon;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { setUrl(p.url); setTouchedName(false); }}
                  className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl border border-slate-200 bg-slate-50 hover:border-sky-300 hover:bg-sky-50 transition-all"
                  data-testid={`url-preset-${p.id}`}
                >
                  <Icon className="h-5 w-5 text-sky-600" />
                  <span className="text-xs font-medium text-slate-700">{p.label}</span>
                </button>
              );
            })}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">URL</Label>
            <Input
              autoFocus
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="rounded-xl h-11 mt-1 bg-slate-50 border-slate-200 text-slate-900 font-mono text-sm"
              data-testid="url-picker-url-input"
            />
          </div>
          <div className="grid grid-cols-[1fr_140px] gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Display name</Label>
              <Input
                value={name}
                onChange={(e) => { setName(e.target.value); setTouchedName(true); }}
                placeholder="e.g. Live sales dashboard"
                className="rounded-xl h-11 mt-1 bg-slate-50 border-slate-200 text-slate-900"
                data-testid="url-picker-name-input"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Duration</Label>
              <div className="flex items-center gap-1 mt-1">
                <Input
                  type="number"
                  min="1"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="rounded-xl h-11 bg-slate-50 border-slate-200 text-slate-900"
                  data-testid="url-picker-duration-input"
                />
                <span className="text-sm text-slate-500">s</span>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Most public sites work out of the box. Some sites (banks, Google services that require sign-in) block being shown in an iframe — those won&apos;t display on the TV. YouTube watch URLs are automatically converted to embed-mode.
          </p>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Button variant="outline" onClick={onClose} className="rounded-full bg-slate-50 border-slate-200 text-slate-900">Cancel</Button>
            <Button
              onClick={submit}
              disabled={!url.trim()}
              className="rounded-full bg-sky-500 hover:bg-sky-600 text-white"
              data-testid="url-picker-add-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" />Add to playlist
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Tiny scaled-down preview that mirrors what's on the TV — used in the design
 * picker grid. Works for BOTH legacy banner-image designs and free-form canvas
 * designs by delegating to MenuRenderer.
 *
 * The tile is 4:3 inside the picker; we render a 1920×1080 canvas and scale it
 * to fit using CSS transform — same trick as the Templates page so colours,
 * fonts, and layered elements look identical to the live render.
 */
function DesignThumb({ design }) {
  const cfg = design?.config;
  // 1) For elements-based / free-form designs, the cheapest and most accurate
  //    preview is a live MenuRenderer scaled to fit.
  if (cfg && (Array.isArray(cfg.elements) || cfg.layout)) {
    return (
      <div className="relative w-full h-full">
        <div
          className="absolute inset-0 pointer-events-none origin-top-left"
          style={{
            width: "1920px",
            height: "1080px",
            transform: "scale(0.167)", // ~320px wide thumbnail
          }}
        >
          <MenuRenderer config={cfg} scale="tv" />
        </div>
      </div>
    );
  }
  // 2) Legacy banner_image-only designs — render the optimized thumb URL.
  if (cfg?.banner_image) {
    return <img src={thumbUrl(cfg.banner_image, 320)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />;
  }
  // 3) Nothing to preview — show the name.
  return <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs px-2 text-center">{design.name}</div>;
}

/**
 * Thumbnail for an already-added playlist item (the small 64×48 chip on the
 * left of each item row). For design items we look up the full design object
 * so we can render via MenuRenderer when no thumbnail URL was stored — this
 * is what fixes the "blank thumbnail" bug for elements-based designs.
 */
function ItemThumb({ item, designs }) {
  if (item.kind === "url") {
    return <Globe className="h-5 w-5 text-sky-500" />;
  }
  if (item.kind === "asset") {
    if (item.thumbnail_url) {
      return <img src={thumbUrl(item.thumbnail_url, 160)} className="w-full h-full object-cover" alt="" loading="lazy" decoding="async" />;
    }
    return <span className="text-[10px]">{(item.asset_kind || "asset").toUpperCase()}</span>;
  }
  if (item.kind === "design") {
    if (item.thumbnail_url) {
      return <img src={thumbUrl(item.thumbnail_url, 160)} className="w-full h-full object-cover" alt="" loading="lazy" decoding="async" />;
    }
    // No stored thumbnail — try to render the live design from our preloaded
    // designs list. (The picker has already fetched /designs once.)
    const d = designs?.find((x) => x.id === item.ref_id);
    if (d) return <DesignThumb design={d} />;
    return <span className="text-[10px]">DESIGN</span>;
  }
  return <span className="text-[10px]">{(item.kind || "?").toUpperCase()}</span>;
}

